import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "./CurrencyMasterPage";
import { Field } from "./BankAccountsPage";
import { SegBadge } from "./OpeningBalancesPage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function MonthlyPnLPage() {
    const { hasPerm } = useAuth();
    const canWrite = hasPerm("monthly_pnl", "write");
    const [rows, setRows] = useState([]);
    const [form, setForm] = useState({
        segment: "Voice",
        month: new Date().toISOString().slice(0, 7),
        cogs: "",
    });

    const load = async () => {
        const { data } = await api.get("/monthly-pnl");
        setRows(data);
    };
    useEffect(() => {
        load();
    }, []);

    const submit = async () => {
        if (!form.month || !form.segment) return toast.error("Month and segment required");
        try {
            await api.post("/monthly-pnl", {
                segment: form.segment,
                month: form.month,
                cogs: parseFloat(form.cogs || 0),
                overheads: 0,
            });
            toast.success("COGS saved");
            setForm({ ...form, cogs: "" });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    return (
        <div data-testid="monthly-pnl-page">
            <PageHeader
                title="Monthly COGS"
                eyebrow="Reporting"
                description="Enter month-end Cost of Goods Sold per segment. Voice values are stored in USD; SMS in EUR. Overheads are aggregated automatically from Payments tagged to Overhead Ledger accounts."
            />

            {canWrite && (
                <div
                    className="bg-white border border-slate-200 p-5 mb-6 grid md:grid-cols-4 gap-3 items-end"
                    data-testid="pnl-input-card"
                >
                    <Field label="Segment">
                        <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
                            <SelectTrigger className="rounded-sm" data-testid="pnl-segment-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Voice">Voice (USD)</SelectItem>
                                <SelectItem value="SMS">SMS (EUR)</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Month (YYYY-MM)">
                        <Input
                            type="month"
                            value={form.month}
                            onChange={(e) => setForm({ ...form, month: e.target.value })}
                            className="rounded-sm h-9 font-mono"
                            data-testid="pnl-month-input"
                        />
                    </Field>
                    <Field label="COGS">
                        <Input
                            type="number"
                            step="0.01"
                            value={form.cogs}
                            onChange={(e) => setForm({ ...form, cogs: e.target.value })}
                            className="rounded-sm h-9 font-mono"
                            data-testid="pnl-cogs-input"
                        />
                    </Field>
                    <Button
                        onClick={submit}
                        className="rounded-sm bg-[#002FA7] hover:bg-[#001f6f] h-9"
                        data-testid="pnl-save-btn"
                    >
                        Save
                    </Button>
                </div>
            )}

            <div className="bg-white border border-slate-200 overflow-hidden">
                <table className="w-full text-[13px] financial">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
                            <th className="px-4 py-2.5">Month</th>
                            <th className="px-4 py-2.5">Segment</th>
                            <th className="px-4 py-2.5 num">COGS</th>
                            <th className="px-4 py-2.5">Base</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={4} className="text-center text-slate-500 py-10">
                                    No entries yet
                                </td>
                            </tr>
                        )}
                        {rows.map((r, i) => (
                            <tr
                                key={`${r.segment}-${r.month}-${i}`}
                                className="border-b border-slate-100"
                                data-testid={`pnl-row-${r.segment}-${r.month}`}
                            >
                                <td className="px-4 py-2 font-mono">{r.month}</td>
                                <td className="px-4 py-2">
                                    <SegBadge segment={r.segment} />
                                </td>
                                <td className="px-4 py-2 num">{Number(r.cogs).toFixed(2)}</td>
                                <td className="px-4 py-2 font-mono text-slate-500">{r.base_currency}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
